import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
const MODULES = ["Production", "Machine Shop", "Final Inspection", "Quality", "Rejections", "Inventory"];
const MARKERS = [
  { value: "End-to-end", label: "Floor → finished goods" },
  { value: "Real-time", label: "Live on every shift" },
  { value: "IATF 16949", label: "Quality assured" },
];
export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Deep-link return target (e.g. a scanned /trace/{token}) passed by ProtectedRoute.
  const from = location.state?.from || null;
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await login(email, password, remember);
      toast.success("Login successful");
      navigate("/select-unit", from ? { state: { from } } : undefined);
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen flex bg-white">
      {/* Hero */}
      <div className="hidden lg:flex lg:w-[56%] relative overflow-hidden bg-slate-950">
        <img src="/facility.jpg" alt="Anmol facility" className="absolute inset-0 w-full h-full object-cover" />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(105deg, rgba(9,14,30,.94) 0%, rgba(11,18,42,.72) 42%, rgba(11,18,42,.28) 100%)" }}
        />
        <div className="relative z-10 flex flex-col justify-between p-14 w-full text-white">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Anmol" className="h-10 w-auto drop-shadow" />
            <span className="font-mono text-xs tracking-[0.22em] uppercase text-white/60">AAMPL · Since 1996</span>
          </div>
          <div className="max-w-xl">
            <div className="h-1 rounded mb-6 bg-red-600" style={{ width: 52 }} />
            <h1 className="text-5xl font-bold leading-[1.06] tracking-tight mb-5">
              One platform, from shop floor to finished goods.
            </h1>
            <p className="text-lg text-slate-200/80 max-w-md mb-8">
              Production, machine shop, inspection, quality, rejections and full inventory — managed end to end, in real time.
            </p>
            <div className="flex flex-wrap gap-2 mb-9">
              {MODULES.map((m) => (
                <span key={m} className="font-mono text-xs text-slate-100 border border-white/20 bg-white/5 px-3 py-1.5 rounded-full">
                  {m}
                </span>
              ))}
            </div>
            <div className="flex gap-10">
              {MARKERS.map((x) => (
                <div key={x.value}>
                  <div className="font-semibold text-[22px]">{x.value}</div>
                  <div className="font-mono text-[11px] tracking-wide uppercase text-slate-200/60 mt-1.5">{x.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F7F9FC]">
        <div className="w-full max-w-md">
          <img src="/logo.png" alt="Logo" className="h-14 w-auto mb-6" />
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-1.5">Operations Platform</h2>
          <p className="text-slate-500 mb-8">Advanced Anmol Metcomp Pvt. Ltd.</p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="Enter your email" value={email}
                onChange={(e) => setEmail(e.target.value)} className="h-12 bg-white text-slate-900 placeholder:text-slate-400" data-testid="login-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} placeholder="Enter your password"
                  value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 pr-11 bg-white text-slate-900 placeholder:text-slate-400" data-testid="login-password" />
                <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-12 w-11"
                  onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600 select-none">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 rounded accent-blue-600" />
                Remember me
              </label>
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>
            <Button type="submit" className="w-full h-12 text-base font-semibold" disabled={loading} data-testid="login-submit">
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
          <div className="flex items-center gap-2 mt-7 pt-5 border-t border-slate-200">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            <span className="font-mono text-xs text-slate-400">Secured · IATF 16949 environment</span>
          </div>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle>Forgot your password?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 leading-relaxed">
            Password resets are done by your plant administrator. Ask them to reset it from
            User Management — you'll sign in with the new password immediately after.
            Your data is unaffected.
          </p>
          <DialogFooter>
            <Button onClick={() => setForgotOpen(false)} data-testid="forgot-password-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
